"use client"

import {
  useState,
  useEffect,
  useMemo,
  useRef,
  useLayoutEffect,
  useCallback,
} from "react"
import { createPortal } from "react-dom"
import { AnimatePresence, motion } from "framer-motion"
import {
  MagnifyingGlass,
  Plus,
  FunnelSimple,
  DotsThreeOutlineVertical,
  X,
  ArrowClockwise,
  WarningCircle,
  SpinnerGap,
  CalendarBlank,
  CreditCard,
  Activity,
  Globe,
  User,
  PlusCircle,
  ArrowRight,
  CaretLeft,
  CaretRight,
  CaretDoubleLeft,
  CaretDoubleRight,
  Cursor,
  ArrowDown,
  Check,
  ShieldWarning,
  Buildings,
} from "phosphor-react"

import { DateRangePicker, SearchableSelect } from "../../components"
import NewAdAccountModal from "./NewAdAccountModal"
import AddBalanceModal from "./AddBalanceModal"
import ChangeStatusModal from "./ChangeStatusModal"
import TransferFundsModal from "./TransferFundsModal"
import WithdrawBalanceModal from "./TopDown"
import adAccountsService from "../../services/adAccountsService"
import creditAccountsService from "../../services/creditAccountsService"
import clientsService from "../../services/clientsService"
import { useCurrentUser } from "../../utils/context/UserProvider"
import { Link } from "react-router-dom"
import { useAccessChecker } from "../../utils/Functions/Authorization"
import toast from "react-hot-toast"

export default function AdAccountsnew() {
  const { checkAccess } = useAccessChecker()
  const { currentUser } = useCurrentUser()

  // Data
  const [adAccounts, setAdAccounts] = useState([])
  const [loading, setLoading] = useState(false)
  const [loadingOptions, setLoadingOptions] = useState(false)

  // ✅ Top stats cards (from backend: response.data.stats)
  const [stats, setStats] = useState({
    totalAdAccounts: 0,
    totalActive: 0,
    totalBanned: 0,
  })

  // Filters
  const [searchQuery, setSearchQuery] = useState("")
  const [dateRange, setDateRange] = useState({ start: "", end: "" })
  const [showMoreFilters, setShowMoreFilters] = useState(false)
  const [filters, setFilters] = useState({
    creditAccount: "",
    client: "",
    status: "",
    platform: "",
  })

  // Options
  const [accountOptions, setAccountOptions] = useState([])
  const [userOptions, setUserOptions] = useState([])

  // Modals
  const [showNewAccountModal, setShowNewAccountModal] = useState(false)
  const [showAddBalanceModal, setShowAddBalanceModal] = useState(false)
  const [showChangeStatusModal, setShowChangeStatusModal] = useState(false)
  const [showTransferFundsModal, setShowTransferFundsModal] = useState(false)
  const [showWithdrawBalanceModal, setShowWithdrawBalanceModal] = useState(false)
  const [selectedAccount, setSelectedAccount] = useState(null)
  const [isEditing, setIsEditing] = useState(false)
  const [done, setDone] = useState(false)

  // Pagination
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 5,
    totalPages: 1,
    totalItems: 0,
  })

  // ✅ Dropdown (Portal like Clients page)
  const [dropdownOpen, setDropdownOpen] = useState(null) // accountId
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 })
  const [anchorRect, setAnchorRect] = useState(null)
  const menuRef = useRef(null)
  const btnRefs = useRef(new Map()) // accountId -> button element

  const filtersActive = useMemo(() => {
    return (
      searchQuery !== "" ||
      dateRange.start !== "" ||
      dateRange.end !== "" ||
      filters.creditAccount !== "" ||
      filters.client !== "" ||
      filters.status !== "" ||
      filters.platform !== ""
    )
  }, [searchQuery, dateRange, filters])

  /* ───────────────────────────── Title ───────────────────────────── */
  useEffect(() => {
    const event = new CustomEvent("setPageTitle", {
      detail: { title: "Ad Accounts Management" },
    })
    window.dispatchEvent(event)

    return () => {
      const resetEvent = new CustomEvent("setPageTitle", {
        detail: { title: "Dashboard" },
      })
      window.dispatchEvent(resetEvent)
    }
  }, [])

  /* ───────────────────────────── Fetch ───────────────────────────── */

  const fetchAdAccounts = useCallback(
    async (overrides = {}) => {
      try {
        setLoading(true)

        const page = overrides.page ?? pagination.currentPage
        const limit = overrides.limit ?? pagination.pageSize

        const payload = {
          ...filters,
          searchValue: searchQuery,
          dateRange,
          pagination: { page, limit },
        }

        const response = await adAccountsService.search(payload)
        const r = response.data

        if (r?.statusCode === 200) {
          setAdAccounts(r.data || [])

          if (r.pagination) {
            setPagination((p) => ({
              ...p,
              currentPage: r.pagination.currentPage,
              pageSize: r.pagination.pageSize,
              totalPages: r.pagination.totalPages,
              totalItems: r.pagination.totalItems,
            }))
          }

          // ✅ Stats from backend (recommended keys)
          const s = r.stats || r.totals || {}
          setStats({
            totalAdAccounts: Number(s.totalAdAccounts ?? s.totalAccounts ?? 0),
            totalActive: Number(s.totalActive ?? 0),
            totalBanned: Number(s.totalBanned ?? 0),
          })
        }
      } catch (error) {
        console.error("Failed to fetch ad accounts:", error)
        toast.error(error.response?.data?.message || "Failed to load ad accounts")
      } finally {
        setLoading(false)
      }
    },
    [filters, searchQuery, dateRange, pagination.currentPage, pagination.pageSize]
  )

  const fetchCreditAccounts = useCallback(async () => {
    try {
      setLoadingOptions(true)

      const response = await creditAccountsService.searchAccounts()
      const r = response.data

      if (r?.statusCode === 200) {
        const uniqueAccountsMap = new Map()

          ; (r.data || []).forEach((item) => {
            const userId = item.user?._id
            if (userId && !uniqueAccountsMap.has(userId)) {
              uniqueAccountsMap.set(userId, {
                id: item.account_name,
                name: item.account_name,
                value: item._id,
                balance: item.balance,
                currency: item.account_currency,
                clientName: item.user?.reference || item.user?.fullName || "",
              })
            }
          })

        setAccountOptions(Array.from(uniqueAccountsMap.values()))
      }
    } catch (error) {
      console.error("Failed to fetch accounts:", error)
      toast.error(error.response?.data?.message || "Failed to load credit accounts")
    } finally {
      setLoadingOptions(false)
    }
  }, [])

  const fetchClients = useCallback(async () => {
    try {
      setLoadingOptions(true)

      const response = await clientsService.searchClients()
      const r = response.data

      if (r?.statusCode === 200) {
        const dataClients = (r.data || []).map((item) => ({
          id: item._id,
          name: item.reference || item.fullName,
          value: item._id,
        }))
        setUserOptions(dataClients)
      }
    } catch (error) {
      console.error("Failed to fetch clients:", error)
      toast.error(error.response?.data?.message || "Failed to load clients")
    } finally {
      setLoadingOptions(false)
    }
  }, [])

  useEffect(() => {
    fetchAdAccounts()
    fetchCreditAccounts()
    fetchClients()
    setIsEditing(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [done, pagination.currentPage, pagination.pageSize])

  /* ───────────────────────────── Helpers ───────────────────────────── */

  const handleFilterChange = (filterName, value) => {
    setFilters((p) => ({ ...p, [filterName]: value }))
  }

  const handleDateRangeChange = (range) => {
    setDateRange(range)
  }

  const clearFilters = () => {
    setFilters({ creditAccount: "", client: "", status: "", platform: "" })
    setSearchQuery("")
    setDateRange({ start: "", end: "" })
  }

  const applyFilters = () => {
    setPagination((p) => ({ ...p, currentPage: 1 }))
    fetchAdAccounts({ page: 1 })
  }

  const resetAndRefresh = () => {
    clearFilters()
    setPagination((p) => ({ ...p, currentPage: 1 }))
    setTimeout(() => fetchAdAccounts({ page: 1 }), 0)
  }

  const handleSearch = () => {
    setPagination((p) => ({ ...p, currentPage: 1 }))
    fetchAdAccounts({ page: 1 })
  }

  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > pagination.totalPages) return
    setPagination((p) => ({ ...p, currentPage: newPage }))
  }

  const handlePageSizeChange = (e) => {
    const newSize = Number.parseInt(e.target.value)
    setPagination((p) => ({ ...p, pageSize: newSize, currentPage: 1 }))
    setTimeout(() => fetchAdAccounts({ page: 1, limit: newSize }), 0)
  }

  const formatCurrency = (amount, currency = "USD") => {
    return new Intl.NumberFormat("en-US", { style: "currency", currency }).format(amount || 0)
  }

  const formatDate = (raw) => {
    if (!raw) return "N/A"
    const dateString = String(raw).trim()
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone

    const dotDateRE = /^(\d{1,2})\.(\d{1,2})\.(\d{4})(?:\s+(\d{1,2}):(\d{2}))?$/
    const hasExplicitTime =
      /T\d{2}:\d{2}/.test(dateString) || /\s\d{1,2}:\d{2}/.test(dateString)

    let d
    const m = dateString.match(dotDateRE)
    if (m) {
      const [, dd, mm, yyyy, HH, MM] = m
      d = new Date(
        parseInt(yyyy, 10),
        parseInt(mm, 10) - 1,
        parseInt(dd, 10),
        HH ? parseInt(HH, 10) : 0,
        MM ? parseInt(MM, 10) : 0,
        0,
        0
      )
    } else {
      d = new Date(dateString)
    }
    if (isNaN(d.getTime())) return raw

    const datePart = new Intl.DateTimeFormat("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      timeZone,
    }).format(d)

    if (!hasExplicitTime) return datePart

    const timeFormatter = new Intl.DateTimeFormat("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
      timeZone,
    })

    const parts = timeFormatter.formatToParts(d)
    const hour = parts.find((p) => p.type === "hour")?.value ?? "00"
    const minute = parts.find((p) => p.type === "minute")?.value ?? "00"
    const hourNum = parseInt(hour, 10)
    const suffix = hourNum < 12 ? "AM" : "PM"
    const timePart = `${hour}:${minute} ${suffix}`

    return `${datePart}, ${timePart}`
  }

  const normalizeStatus = (status) => String(status || "").trim().toLowerCase()

  const getStatusBadgeColor = (status) => {
    const s = normalizeStatus(status)
    switch (s) {
      case "active":
        return "bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-400 border border-green-100 dark:border-green-800/60"
      case "pending":
        return "bg-yellow-50 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400 border border-yellow-100 dark:border-yellow-800/60"
      case "suspended":
        return "bg-orange-50 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400 border border-orange-100 dark:border-orange-800/60"
      case "reinstated":
        return "bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-400 border border-green-100 dark:border-green-800/60"
      case "banned":
        return "bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-400 border border-red-100 dark:border-red-800/60"
      default:
        return "bg-neutral-50 text-neutral-700 dark:bg-neutral-900/40 dark:text-neutral-300 border border-neutral-100 dark:border-neutral-800/70"
    }
  }

  const getStatusDotClass = (status) => {
    const s = normalizeStatus(status)
    if (s === "active" || s === "reinstated") return "bg-green-500"
    if (s === "pending") return "bg-yellow-500"
    if (s === "suspended") return "bg-orange-500"
    if (s === "banned") return "bg-red-500"
    return "bg-neutral-500"
  }

  const getPageNumbers = (currentPage, totalPages) => {
    if (totalPages <= 5) return Array.from({ length: totalPages }, (_, i) => i + 1)
    if (currentPage <= 3) return [1, 2, 3, 4, "...", totalPages]
    if (currentPage >= totalPages - 2)
      return [1, "...", totalPages - 3, totalPages - 2, totalPages - 1, totalPages]
    return [1, "...", currentPage - 1, currentPage, currentPage + 1, "...", totalPages]
  }

  // Filter options
  const statusOptions = [
    { id: 1, name: "All Statuses", value: "" },
    { id: 2, name: "Active", value: "active" },
    { id: 3, name: "Pending", value: "pending" },
    { id: 4, name: "Reinstated", value: "reinstated" },
    { id: 5, name: "Suspended", value: "suspended" },
    { id: 6, name: "Banned", value: "banned" },
  ]

  const platformOptions = [
    { id: 1, name: "All Platforms", value: "" },
    { id: 2, name: "Meta", value: "meta" },
    { id: 3, name: "Google", value: "google" },
    { id: 4, name: "TikTok", value: "tiktok" },
    { id: 5, name: "Snapchat", value: "snapchat" },
  ]

  /* ───────────────────────────── Dropdown (Portal) ───────────────────────────── */

  const closeDropdown = useCallback(() => setDropdownOpen(null), [])
  const openDropdown = useCallback((accountId) => {
    const btn = btnRefs.current.get(accountId)
    if (!btn) return

    const r = btn.getBoundingClientRect()
    setAnchorRect(r)

    const menuW = 208 // a bit wider than w-48
    const gap = 8
    const pad = 8

    let left = r.right - menuW
    if (left < pad) left = r.left
    left = Math.min(Math.max(left, pad), window.innerWidth - menuW - pad)

    const top = r.bottom + gap
    setDropdownPos({ top, left })
    setDropdownOpen(accountId)
  }, [])

  const toggleDropdown = useCallback(
    (accountId) => {
      if (dropdownOpen === accountId) return closeDropdown()
      openDropdown(accountId)
    },
    [dropdownOpen, closeDropdown, openDropdown]
  )

  useLayoutEffect(() => {
    if (!dropdownOpen || !menuRef.current || !anchorRect) return

    const menu = menuRef.current
    const pad = 8
    const gap = 8
    const mh = menu.getBoundingClientRect().height

    let top = anchorRect.bottom + gap
    if (top + mh > window.innerHeight - pad) {
      top = Math.max(pad, anchorRect.top - mh - gap)
    }

    if (Math.abs(top - dropdownPos.top) > 1) {
      setDropdownPos((p) => ({ ...p, top }))
    }
  }, [dropdownOpen, anchorRect, dropdownPos.top])

  useEffect(() => {
    if (!dropdownOpen) return

    const onMouseDown = (e) => {
      const menu = menuRef.current
      const btn = btnRefs.current.get(dropdownOpen)
      if (menu && menu.contains(e.target)) return
      if (btn && btn.contains(e.target)) return
      closeDropdown()
    }

    const onKeyDown = (e) => {
      if (e.key === "Escape") closeDropdown()
    }

    const onAnyScroll = () => closeDropdown()

    window.addEventListener("mousedown", onMouseDown)
    window.addEventListener("keydown", onKeyDown)
    window.addEventListener("scroll", onAnyScroll, true)
    window.addEventListener("resize", onAnyScroll)

    return () => {
      window.removeEventListener("mousedown", onMouseDown)
      window.removeEventListener("keydown", onKeyDown)
      window.removeEventListener("scroll", onAnyScroll, true)
      window.removeEventListener("resize", onAnyScroll)
    }
  }, [dropdownOpen, closeDropdown])

  /* ───────────────────────────── Actions ───────────────────────────── */

  const handleCreate = () => setShowNewAccountModal(true)

  const handleFormSuccess = () => setDone((d) => !d)

  const onCloseAccountModal = () => {
    setShowNewAccountModal(false)
    setTimeout(() => {
      setSelectedAccount(null)
      setIsEditing(false)
    }, 400)
  }

  const handleAddBalance = (account) => {
    setSelectedAccount(account)
    setShowAddBalanceModal(true)
    closeDropdown()
  }

  const handleChangeStatus = (account) => {
    setSelectedAccount(account)
    setShowChangeStatusModal(true)
    closeDropdown()
  }

  const handleWithdrawBalance = (account) => {
    setSelectedAccount(account)
    setShowWithdrawBalanceModal(true)
    closeDropdown()
  }

  /* ───────────────────────────── Render ───────────────────────────── */

  return (
    <div className="lg:p-7 p-3 max-w-full mx-auto">
      {/* ✅ Top Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 mb-6">
        <StatCard
          title="Total Ad Accounts"
          value={stats.totalAdAccounts}
          icon={<Buildings className="text-yellow-500" size={18} />}
          color="neutral"
        />
        <StatCard
          title="Active Ad Accounts"
          value={stats.totalActive}
          icon={<Check className="text-green-700" size={18} />}
          color="green"
        />
        <StatCard
          title="Banned Ad Accounts"
          value={stats.totalBanned}
          icon={<ShieldWarning className="text-red-700" size={18} />}
          color="red"
        />
      </div>

      {/* Search and filters top bar */}
      <div className="flex flex-wrap gap-3 mb-6 items-center">
        {/* Search input */}
        <div className="relative flex-grow max-w-xs">
          <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
            <MagnifyingGlass size={16} className="text-neutral-800 dark:text-neutral-400" />
          </div>
          <input
            type="text"
            placeholder="Search ad accounts..."
            className="h-10 w-full border pl-10 pr-10 text-sm border-neutral-150 dark:border-neutral-900 dark:bg-neutral-900/30 rounded-xl placeholder-neutral-500 cursor-pointer ring-neutral-300 dark:ring-neutral-700 bg-white dark:bg-neutral-950 text-neutral-600 dark:text-neutral-200 focus:outline-none focus:ring-1"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
          />
          <button
            type="button"
            className="absolute inset-y-0 right-0 flex items-center pr-3 text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-300 transition-colors"
            onClick={handleSearch}
            aria-label="Submit search"
          >
            <ArrowRight size={16} />
          </button>
        </div>

        {/* Date range picker */}
        <DateRangePicker
          onRangeChange={handleDateRangeChange}
          value={dateRange}
          className={
            dateRange.start || dateRange.end
              ? "border-blue-500 bg-blue-50 text-blue-600 dark:border-blue-600 dark:bg-blue-900/20 dark:text-blue-400"
              : ""
          }
        />

        {/* More filters */}
        <button
          className={`flex items-center ring-yellow-500/50 gap-2 px-4 py-2.5 h-10 w-fit border text-sm rounded-xl cursor-pointer focus:outline-none focus:ring-1 ${showMoreFilters
              ? "border-yellow-500/50 bg-yellow-50 text-yellow-600 dark:border-yellow-600/30 dark:bg-yellow-900/20 dark:text-yellow-400"
              : "border-neutral-150 dark:border-neutral-900 dark:bg-neutral-900/30 bg-white dark:bg-neutral-950 text-neutral-600 dark:text-neutral-200"
            }`}
          onClick={() => setShowMoreFilters((v) => !v)}
        >
          <span className="text-sm">{showMoreFilters ? "Hide filters" : "More filters"}</span>
          {showMoreFilters ? (
            <X size={16} className="text-yellow-600 dark:text-yellow-400" />
          ) : (
            <PlusCircle size={16} className="text-neutral-800 dark:text-neutral-400" />
          )}
        </button>

        {/* Reset */}
        <button
          className="flex items-center gap-2 h-10 px-4 border border-neutral-150 dark:border-neutral-900 dark:bg-neutral-900/30 rounded-xl bg-white dark:bg-neutral-950 text-neutral-800 dark:text-white hover:bg-neutral-50 dark:hover:bg-neutral-800"
          onClick={resetAndRefresh}
          aria-label="Reset filters"
        >
          <ArrowClockwise size={16} className="text-neutral-800 dark:text-neutral-400" />
          <span className="text-sm">Reset</span>
        </button>

        {/* Apply filters button */}
        {filtersActive && !showMoreFilters && (
          <button
            className="flex items-center text-xs justify-center px-4 gap-2 h-10 bg-neutral-900 text-white dark:bg-neutral-100 dark:text-black rounded-xl hover:scale-105 transition-all duration-300"
            onClick={applyFilters}
          >
            <FunnelSimple size={16} />
            <span className="text-xs">Apply Filters</span>
          </button>
        )}

        {/* Create */}
        {checkAccess("adAccounts", "create") && (
          <button
            className="flex items-center gap-2 px-4 h-10 bg-neutral-900 text-white dark:bg-neutral-100 dark:text-black rounded-xl hover:scale-105 transition-all duration-300 ml-auto"
            onClick={handleCreate}
          >
            <Plus size={16} />
            <span className="text-sm">Create Ad Account</span>
          </button>
        )}
      </div>

      {/* ✅ Active Filters Chips */}
      {filtersActive && (
        <div className="mb-4 flex flex-wrap gap-2 items-center">
          <span className="text-sm text-neutral-500 dark:text-neutral-400">Active filters:</span>

          {searchQuery && (
            <Chip
              icon={<MagnifyingGlass size={12} />}
              label={searchQuery}
              onClear={() => {
                setSearchQuery("")
                handleSearch()
              }}
            />
          )}

          {(dateRange.start || dateRange.end) && (
            <Chip
              icon={<CalendarBlank size={12} />}
              label={`${dateRange.start ? new Date(dateRange.start).toLocaleDateString() : "Any"} - ${dateRange.end ? new Date(dateRange.end).toLocaleDateString() : "Any"
                }`}
              onClear={() => {
                setDateRange({ start: "", end: "" })
                handleSearch()
              }}
            />
          )}

          {filters.creditAccount && (
            <Chip
              icon={<CreditCard size={12} />}
              label={
                accountOptions.find((acc) => acc.value === filters.creditAccount)?.name || filters.creditAccount
              }
              onClear={() => {
                handleFilterChange("creditAccount", "")
                applyFilters()
              }}
            />
          )}

          {filters.client && (
            <Chip
              icon={<User size={12} />}
              label={userOptions.find((u) => u.value === filters.client)?.name || filters.client}
              onClear={() => {
                handleFilterChange("client", "")
                applyFilters()
              }}
            />
          )}

          {filters.status && (
            <Chip
              icon={<Activity size={12} />}
              label={filters.status.charAt(0).toUpperCase() + filters.status.slice(1)}
              onClear={() => {
                handleFilterChange("status", "")
                applyFilters()
              }}
            />
          )}

          {filters.platform && (
            <Chip
              icon={<Globe size={12} />}
              label={filters.platform.charAt(0).toUpperCase() + filters.platform.slice(1)}
              onClear={() => {
                handleFilterChange("platform", "")
                applyFilters()
              }}
            />
          )}

          <button
            onClick={resetAndRefresh}
            className="text-xs text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-300 flex items-center gap-1 ml-2"
          >
            <X size={12} />
            <span>Clear all</span>
          </button>
        </div>
      )}

      {/* Expanded filters section */}
      {showMoreFilters && (
        <div className="mb-6 p-6 border border-neutral-150 dark:border-neutral-900 rounded-xl bg-white dark:bg-neutral-950/80">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-[300] text-neutral-800 dark:text-white">More filters</h2>
            <button
              onClick={clearFilters}
              className="text-sm text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-300 flex items-center gap-1.5"
            >
              <X size={14} />
              <span>Clear all</span>
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
            <SearchableSelect
              options={accountOptions}
              value={filters.creditAccount}
              onChange={(value) => handleFilterChange("creditAccount", value)}
              placeholder="Credit Account"
              icon={<CreditCard size={16} />}
              isLoading={loadingOptions}
            />

            {!["Client"].includes(currentUser?.role?.name) && (
              <SearchableSelect
                options={userOptions}
                value={filters.client}
                onChange={(value) => handleFilterChange("client", value)}
                placeholder="Client"
                icon={<User size={16} />}
                isLoading={loadingOptions}
              />
            )}

            <SearchableSelect
              options={statusOptions}
              value={filters.status}
              onChange={(value) => handleFilterChange("status", value)}
              placeholder="Status"
              icon={<Activity size={16} />}
            />

            <SearchableSelect
              options={platformOptions}
              value={filters.platform}
              onChange={(value) => handleFilterChange("platform", value)}
              placeholder="Platform"
              icon={<Globe size={16} />}
            />

            <div className="flex items-center gap-2">
              <button
                className="flex items-center justify-center px-4 gap-2 h-10 bg-neutral-900 text-white dark:bg-neutral-100 dark:text-black rounded-xl hover:scale-105 transition-all duration-300"
                onClick={applyFilters}
              >
                <FunnelSimple size={16} />
                <span className="text-sm">Apply Filters</span>
              </button>

              <button
                className="flex items-center justify-center px-4 gap-2 h-10 border border-neutral-200 dark:border-neutral-900 rounded-xl bg-white dark:bg-neutral-950/80 text-neutral-800 dark:text-white hover:bg-neutral-50 dark:hover:bg-neutral-700"
                onClick={resetAndRefresh}
              >
                <ArrowClockwise size={16} />
                <span className="text-sm">Reset</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="border border-neutral-150 dark:border-neutral-900 dark:bg-neutral-900/30 rounded-lg bg-white">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <SpinnerGap
              size={30}
              className="animate-[spin_0.8s_linear_infinite] text-neutral-600 dark:text-neutral-400 drop-shadow-sm"
            />
          </div>
        ) : adAccounts.length > 0 ? (
          <>
            <div className="overflow-x-auto rounded-t-lg">
              <table className="min-w-full whitespace-nowrap divide-y divide-neutral-150 dark:divide-neutral-800">
                <thead className="bg-neutral-50 text-start h-14 dark:bg-neutral-900/30">
                  <tr>
                    <Th>Created Time</Th>
                    {!["Client"].includes(currentUser?.role?.name) && <Th>Client Reference</Th>}
                    <Th>Platform</Th>
                    <Th>Account Name</Th>
                    <Th>Account ID</Th>
                    <Th>Category</Th>
                    <Th>Status</Th>
                    <Th>Actions</Th>
                  </tr>
                </thead>

                <tbody className="bg-white font-[300] dark:bg-neutral-950 divide-y divide-neutral-150 dark:divide-neutral-800">
                  {adAccounts.map((account) => {
                    const { icon: platformIcon, name: platformName } = IconForPlatform({
                      platform: account.platform,
                      className: "w-5 h-5",
                    })

                    return (
                      <tr
                        key={account._id}
                        className="hover:bg-neutral-50 py-3 text-start dark:hover:bg-neutral-800/50"
                      >
                        <Td className="text-neutral-800 dark:text-white">{formatDate(account.createdAt)}</Td>

                        {!["Client"].includes(currentUser?.role?.name) && (
                          <Td className="text-neutral-800 dark:text-white">
                            {account.user?.reference || account.user?.fullName || "-None-"}
                          </Td>
                        )}

                        <Td>
                          <div className="flex items-center gap-2 text-neutral-800 dark:text-white">
                            <span className="shrink-0">{platformIcon}</span>
                            <span className="text-sm font-[300]">{platformName}</span>
                          </div>
                        </Td>

                        <Td>
                          <Link
                            to={`/ad-accounts/details/${account._id}`}
                            className="text-xs font-[300] text-blue-600 flex justify-start gap-2 items-center dark:bg-blue-600/20 dark:text-blue-300 bg-blue-600/10 w-fit rounded-full px-3 py-1 decoration-blue-500"
                          >
                            <Cursor size={14} className="rotate-90" />
                            <span>{account.account_name || "-None-"}</span>
                          </Link>
                        </Td>

                        <Td className="text-neutral-800 dark:text-white">{account.adAccountId || "-None-"}</Td>

                        <Td className="text-neutral-800 dark:text-white">{account.category || "-None-"}</Td>

                        <Td>
                          <button
                            onClick={() => {
                              if (!["Client"].includes(currentUser?.role?.name)) handleChangeStatus(account)
                            }}
                            className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-[300] ${getStatusBadgeColor(
                              account.status
                            )}`}
                          >
                            <span className={`w-2 h-2 mr-1.5 rounded-full ${getStatusDotClass(account.status)}`} />
                            {(account.status || "unknown").charAt(0).toUpperCase() +
                              (account.status || "unknown").slice(1)}
                          </button>
                        </Td>

                        <Td className="account-dropdown">
                          {!["Client"].includes(currentUser?.role?.name) ? (
                            <button
                              ref={(node) => {
                                if (!node) btnRefs.current.delete(account._id)
                                else btnRefs.current.set(account._id, node)
                              }}
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation()
                                toggleDropdown(account._id)
                              }}
                              className="p-1.5 rounded-full hover:bg-neutral-100 dark:hover:bg-neutral-800 text-neutral-600 dark:text-neutral-400"
                            >
                              <DotsThreeOutlineVertical size={16} weight="bold" />
                            </button>
                          ) : (
                            <button
                              onClick={() => handleAddBalance(account)}
                              disabled={!["active", "reinstated"].includes(normalizeStatus(account?.status))}
                              className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-xl text-xs border border-neutral-200 dark:border-neutral-800 ${!["active", "reinstated"].includes(normalizeStatus(account?.status))
                                  ? "opacity-50 cursor-not-allowed"
                                  : "hover:bg-neutral-50 dark:hover:bg-neutral-900/60"
                                }`}
                            >
                              <Plus size={14} />
                              Top-Up
                            </button>
                          )}
                        </Td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="px-6 py-3 flex flex-col sm:flex-row items-center justify-between border-t border-neutral-150 dark:border-neutral-900 dark:bg-neutral-900/30">
              <div className="flex items-center mb-4 sm:mb-0">
                <span className="text-sm text-neutral-600 dark:text-neutral-400">
                  Showing {adAccounts.length} of {pagination.totalItems} accounts
                </span>

                <div className="ml-4">
                  <select
                    value={pagination.pageSize}
                    onChange={handlePageSizeChange}
                    className="h-8 px-2 text-xs border border-neutral-200 dark:bg-neutral-800 rounded-xl bg-white dark:border-neutral-900 dark:bg-neutral-900/30 text-neutral-800 dark:text-white"
                  >
                    <option value={5}>5 / page</option>
                    <option value={10}>10 per page</option>
                    <option value={25}>25 per page</option>
                    <option value={50}>50 per page</option>
                    <option value={100}>100 per page</option>
                  </select>
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <PageBtn
                  disabled={pagination.currentPage === 1}
                  onClick={() => handlePageChange(1)}
                  aria="First page"
                >
                  <CaretDoubleLeft size={16} />
                </PageBtn>

                <PageBtn
                  disabled={pagination.currentPage === 1}
                  onClick={() => handlePageChange(pagination.currentPage - 1)}
                  aria="Previous page"
                >
                  <CaretLeft size={16} />
                </PageBtn>

                <div className="flex items-center space-x-1">
                  {getPageNumbers(pagination.currentPage, pagination.totalPages).map((page, index) =>
                    page === "..." ? (
                      <span key={`ellipsis-${index}`} className="px-2 py-1 text-neutral-500 dark:text-neutral-400">
                        ...
                      </span>
                    ) : (
                      <button
                        key={page}
                        onClick={() => handlePageChange(page)}
                        className={`w-8 h-8 flex items-center justify-center rounded-xl text-sm ${pagination.currentPage === page
                            ? "bg-neutral-900 text-white dark:bg-neutral-100 dark:text-black"
                            : "text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800"
                          }`}
                      >
                        {page}
                      </button>
                    )
                  )}
                </div>

                <PageBtn
                  disabled={pagination.currentPage === pagination.totalPages || pagination.totalPages === 0}
                  onClick={() => handlePageChange(pagination.currentPage + 1)}
                  aria="Next page"
                >
                  <CaretRight size={16} />
                </PageBtn>

                <PageBtn
                  disabled={pagination.currentPage === pagination.totalPages || pagination.totalPages === 0}
                  onClick={() => handlePageChange(pagination.totalPages)}
                  aria="Last page"
                >
                  <CaretDoubleRight size={16} />
                </PageBtn>
              </div>
            </div>
          </>
        ) : (
          <div className="flex flex-col text-sm items-center justify-center h-64 p-8">
            <div className="p-2 rounded-full bg-neutral-100 dark:bg-neutral-900/20 flex items-center justify-center mb-4">
              <WarningCircle size={24} className="text-neutral-600 dark:text-neutral-400" />
            </div>

            <h3 className="text-xl font-medium text-neutral-900 dark:text-white mb-2">No Ad Accounts Found</h3>

            <p className="text-sm text-neutral-500 dark:text-neutral-400 text-center max-w-md mb-6">
              {searchQuery || Object.values(filters).some(Boolean) || dateRange.start || dateRange.end
                ? "No ad accounts match your search criteria. Try adjusting your filters or search term."
                : "There are no ad accounts in the system yet. Create your first ad account to get started."}
            </p>

            <div className="flex gap-4">
              <button
                onClick={resetAndRefresh}
                className="px-4 py-2 border border-neutral-200 dark:bg-neutral-800 rounded-xl bg-white dark:border-neutral-900 dark:bg-neutral-900/30 text-neutral-800 dark:text-white hover:bg-neutral-50 dark:hover:bg-neutral-700 transition-colors flex items-center gap-2"
              >
                <ArrowClockwise size={16} />
                Reset Filters
              </button>

              {checkAccess("adAccounts", "create") && (
                <button
                  onClick={handleCreate}
                  className="px-4 py-2 bg-neutral-900 text-white dark:bg-neutral-100 dark:text-black rounded-xl hover:scale-105 transition-all duration-300 flex items-center gap-2"
                >
                  <Plus size={16} />
                  <span>Create Ad Account</span>
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ✅ Dropdown Portal ONCE */}
      {typeof document !== "undefined" &&
        createPortal(
          <AnimatePresence>
            {dropdownOpen && (
              <motion.div
                ref={menuRef}
                className="fixed w-52 bg-white dark:bg-neutral-900/70 backdrop-blur-md border border-neutral-200 dark:border-neutral-800 rounded-xl shadow-lg z-[9999] p-1"
                style={{ top: dropdownPos.top, left: dropdownPos.left }}
                initial={{ opacity: 0, scale: 0.98, y: 4 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.98, y: 4 }}
                transition={{ duration: 0.12 }}
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    const acc = adAccounts.find((a) => a._id === dropdownOpen)
                    if (acc) handleChangeStatus(acc)
                    closeDropdown()
                  }}
                  className="flex items-center w-full text-left px-4 py-2 text-sm text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800 rounded-xl"
                >
                  <Activity size={16} className="mr-2" />
                  Change Status
                </button>

                <div className="border-t border-neutral-200 dark:border-neutral-800 my-1" />

                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    const acc = adAccounts.find((a) => a._id === dropdownOpen)
                    if (acc) handleAddBalance(acc)
                    closeDropdown()
                  }}
                  className="flex items-center w-full text-left px-4 py-2 text-sm text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800 rounded-xl"
                >
                  <Plus size={16} className="mr-2" />
                  Top-Up
                </button>

                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    const acc = adAccounts.find((a) => a._id === dropdownOpen)
                    if (acc) handleWithdrawBalance(acc)
                    closeDropdown()
                  }}
                  className="flex items-center w-full text-left px-4 py-2 text-sm text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800 rounded-xl"
                >
                  <ArrowDown size={16} className="mr-2" />
                  Withdraw Request
                </button>
              </motion.div>
            )}
          </AnimatePresence>,
          document.body
        )}

      {/* Modals */}
      <NewAdAccountModal
        isOpen={showNewAccountModal}
        onClose={onCloseAccountModal}
        onSuccess={handleFormSuccess}
        creditAccounts={accountOptions}
        clients={userOptions}
        isEditing={isEditing}
        accountId={selectedAccount}
      />

      {selectedAccount && (
        <>
          <AddBalanceModal
            isOpen={showAddBalanceModal}
            onClose={() => setShowAddBalanceModal(false)}
            account={selectedAccount}
            onSuccess={handleFormSuccess}
          />

          <ChangeStatusModal
            isOpen={showChangeStatusModal}
            onClose={() => setShowChangeStatusModal(false)}
            account={selectedAccount}
            onSuccess={handleFormSuccess}
            setDone={setDone}
            done={done}
          />

          <TransferFundsModal
            isOpen={showTransferFundsModal}
            onClose={() => setShowTransferFundsModal(false)}
            account={selectedAccount}
            onSuccess={handleFormSuccess}
          />

          <WithdrawBalanceModal
            isOpen={showWithdrawBalanceModal}
            onClose={() => setShowWithdrawBalanceModal(false)}
            account={selectedAccount}
            onSuccess={handleFormSuccess}
          />
        </>
      )}
    </div>
  )
}

/* ───────────────────────────── Small UI Components ───────────────────────────── */

function Th({ children }) {
  return (
    <th className="px-6 py-3 text-start text-xs font-[300] text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
      {children}
    </th>
  )
}

function Td({ children, className = "" }) {
  return (
    <td className={`px-6 py-5 whitespace-nowrap text-sm font-[300] text-neutral-800 dark:text-white ${className}`}>
      {children}
    </td>
  )
}

function Chip({ icon, label, onClear }) {
  return (
    <div className="flex items-center gap-1 bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 px-2 py-1 rounded-full text-xs">
      {icon}
      <span>{label}</span>
      <button onClick={onClear} className="ml-1 hover:text-blue-900 dark:hover:text-blue-100">
        <X size={12} />
      </button>
    </div>
  )
}

function PageBtn({ disabled, onClick, children, aria }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`p-1.5 rounded-xl ${disabled
          ? "text-neutral-400 dark:text-neutral-600 cursor-not-allowed"
          : "text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800"
        }`}
      aria-label={aria}
    >
      {children}
    </button>
  )
}

/* ✅ Tailwind-safe colors */
const STAT_COLOR = {
  yellow: { text: "text-yellow-600 dark:text-yellow-600", bg: "bg-yellow-100 dark:bg-yellow-900/30" },
  green: { text: "text-green-600 dark:text-green-600", bg: "bg-green-100 dark:bg-green-900/30" },
  red: { text: "text-red-600 dark:text-red-600", bg: "bg-red-100 dark:bg-red-900/30" },
  neutral: { text: "text-neutral-600 dark:text-neutral-200", bg: "bg-yellow-100 dark:bg-yellow-900/30" },

}

function StatCard({ title, value, icon, color }) {
  const c = STAT_COLOR[color] || STAT_COLOR.yellow
  return (
    <div className="border border-neutral-150 dark:border-neutral-900 dark:bg-neutral-900/30 bg-white dark:bg-neutral-950 rounded-xl p-4">
      <div className="flex items-center justify-between">
        <div className="flex items-start justify-center flex-col">
          <p className="text-sm text-neutral-600 dark:text-neutral-400">{title}</p>
          <p className={`text-2xl font-normal ${c.text}`}>{Number(value || 0).toLocaleString("en-US")}</p>
        </div>
        <div className={`p-2 ${c.bg} rounded-xl`}>{icon}</div>
      </div>
    </div>
  )
}

/* ───────────────────────────── Platform Icon Helper ─────────────────────────────
   Returns { name, icon } so you can show both like the Clients style.
*/
function IconForPlatform({ platform, className = "" }) {
  const p = String(platform || "").toLowerCase()

  switch (p) {
    case "meta":
    case "facebook":
      return {
        name: "Meta",
        icon: (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 48 48"
            className={className}
          >
            <path
              fill="#0081fb"
              d="M47,29.36l-2.193,1.663L42.62,29.5c0-0.16,0-0.33-0.01-0.5c0-0.16,0-0.33-0.01-0.5
              c-0.14-3.94-1.14-8.16-3.14-11.25c-1.54-2.37-3.51-3.5-5.71-3.5c-2.31,0-4.19,1.38-6.27,4.38c-0.06,0.09-0.13,0.18-0.19,0.28
              c-0.04,0.05-0.07,0.1-0.11,0.16c-0.1,0.15-0.2,0.3-0.3,0.46c-0.9,1.4-1.84,3.03-2.86,4.83c-0.09,0.17-0.19,0.34-0.28,0.51
              c-0.03,0.04-0.06,0.09-0.08,0.13l-0.21,0.37l-1.24,2.19c-2.91,5.15-3.65,6.33-5.1,8.26C14.56,38.71,12.38,40,9.51,40
              c-3.4,0-5.56-1.47-6.89-3.69C1.53,34.51,1,32.14,1,29.44l4.97,0.17c0,1.76,0.38,3.1,0.89,3.92C7.52,34.59,8.49,35,9.5,35
              c1.29,0,2.49-0.27,4.77-3.43c1.83-2.53,3.99-6.07,5.44-8.3l1.37-2.09l0.29-0.46l0.3-0.45l0.5-0.77c0.76-1.16,1.58-2.39,2.46-3.57
              c0.1-0.14,0.2-0.28,0.31-0.42c0.1-0.14,0.21-0.28,0.31-0.41c0.9-1.15,1.85-2.22,2.87-3.1c1.85-1.61,3.84-2.5,5.85-2.5
              c3.37,0,6.58,1.95,9.04,5.61c2.51,3.74,3.82,8.4,3.97,13.25c0.01,0.16,0.01,0.33,0.01,0.5C47,29.03,47,29.19,47,29.36z"
            />
            <linearGradient
              id="meta_g1"
              x1="42.304"
              x2="13.533"
              y1="24.75"
              y2="24.75"
              gradientUnits="userSpaceOnUse"
            >
              <stop offset="0" stopColor="#0081fb" />
              <stop offset=".995" stopColor="#0064e1" />
            </linearGradient>
            <path
              fill="url(#meta_g1)"
              d="M4.918,15.456
              C7.195,11.951,10.483,9.5,14.253,9.5c2.184,0,4.354,0.645,6.621,2.493c2.479,2.02,5.122,5.346,8.419,10.828l1.182,1.967
              c2.854,4.746,4.477,7.187,5.428,8.339C37.125,34.606,37.888,35,39,35c2.82,0,3.617-2.54,3.617-5.501L47,29.362
              c0,3.095-0.611,5.369-1.651,7.165C44.345,38.264,42.387,40,39.093,40c-2.048,0-3.862-0.444-5.868-2.333
              c-1.542-1.45-3.345-4.026-4.732-6.341l-4.126-6.879c-2.07-3.452-3.969-6.027-5.068-7.192c-1.182-1.254-2.642-2.754-5.067-2.754
              c-1.963,0-3.689,1.362-5.084,3.465L4.918,15.456z"
            />
          </svg>
        ),
      }

    case "google":
      return {
        name: "Google",
        icon: (
          <svg className={className} viewBox="0 0 24 24">
            <path
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              fill="#4285F4"
            />
            <path
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              fill="#34A853"
            />
            <path
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              fill="#FBBC05"
            />
            <path
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              fill="#EA4335"
            />
          </svg>
        ),
      }

    case "tiktok":
      return {
        name: "TikTok",
        icon: (
          <svg className={className} viewBox="0 0 24 24" fill="currentColor">
            <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
          </svg>
        ),
      }

    case "snapchat":
      return {
        name: "Snapchat", icon: (
          <svg xmlns="http://www.w3.org/2000/svg" x="0px" y="0px" width="20" height="20" viewBox="0 0 48 48">
            <path
              fill="#ffeb3b"
              d="M24,4C12.402,4,3,13.402,3,25c0,11.598,9.402,21,21,21s21-9.402,21-21C45,13.402,35.598,4,24,4z"
            ></path>
            <path
              fill="#fff"
              d="M24,36c-2.451,0-4.045-1.09-4.827-1.612c-0.477-0.317-0.924-0.616-1.317-0.705 c-0.345-0.078-0.685-0.118-1.012-0.118c-0.59,0-1.056,0.103-1.386,0.175c-0.207,0.045-0.385,0.084-0.526,0.084 c-0.143,0-0.298-0.032-0.367-0.267c-0.064-0.218-0.107-0.428-0.148-0.631c-0.116-0.568-0.198-0.92-0.416-0.954 c-2.562-0.401-3.295-0.945-3.464-1.331c-0.024-0.055-0.037-0.11-0.04-0.165c-0.008-0.138,0.09-0.258,0.227-0.283 c3.731-0.614,5.399-4.440,5.467-4.589c0.004-0.009,0.008-0.017,0.012-0.026c0.225-0.456,0.268-0.851,0.131-1.174 c-0.258-0.611-1.099-0.879-1.657-1.058c-0.137-0.044-0.268-0.086-0.371-0.126c-0.504-0.198-1.349-0.612-1.241-1.186 c0.078-0.416,0.615-0.707,1.049-0.707c0.12,0,0.226,0.021,0.316,0.063c0.518,0.242,0.986,0.364,1.392,0.364 c0.558,0,0.802-0.236,0.833-0.266c-0.015-0.273-0.033-0.557-0.051-0.846c-0.119-1.922-0.268-4.313,0.337-5.674 C18.915,13.058,22.304,13,23.104,13h0.792c0.8,0,4.189,0.058,6.254,3.347c0.605,1.359,0.456,3.75,0.337,5.673 c-0.018,0.29-0.036,0.573-0.051,0.847c0.031,0.03,0.275,0.266,0.833,0.266c0.406,0,0.874-0.122,1.392-0.364 c0.09-0.042,0.196-0.063,0.316-0.063c0.433,0,0.971,0.291,1.049,0.707c0.108,0.574-0.737,0.988-1.241,1.186 c-0.103,0.04-0.234,0.082-0.371,0.126c-0.558,0.179-1.399,0.447-1.657,1.058c-0.137,0.323-0.094,0.718,0.131,1.174 c0.004,0.009,0.008,0.017,0.012,0.026c0.068,0.149,1.736,3.975,5.467,4.589c0.137,0.025,0.235,0.145,0.227,0.283 c-0.003,0.055-0.016,0.11-0.04,0.165c-0.169,0.386-0.902,0.93-3.464,1.331c-0.218,0.034-0.224,0.267-0.367,0.267c-0.142,0-0.319-0.039-0.526-0.084 c-0.33-0.072-0.796-0.175-1.386-0.175c-0.327,0-0.667,0.04-1.012,0.118c-0.393,0.089-0.84,0.388-1.317,0.705 C28.045,34.91,26.451,36,24,36z"
            ></path>
          </svg>
        )
      }

    default:
      return { name: platform || "Unknown", icon: <Globe size={18} className="text-neutral-800 dark:text-neutral-400" /> }
  }
}
